import { useState } from 'react';
import { Plug, Droplet, Waves, Clock, SlidersHorizontal, X, ArrowUpDown, Warehouse } from 'lucide-react';
import { useSearchStore } from '../../store/useSearchStore';
import { SPACE_TYPE_LABELS } from '../../lib/fit';

const SPACE_TYPES = ['open', 'covered', 'enclosed'];
const AMPS = [20, 30, 50];

const TOGGLES = [
  { key: 'hasPower', label: 'Power', Icon: Plug },
  { key: 'hasWater', label: 'Water', Icon: Droplet },
  { key: 'hasSewer', label: 'Sewer', Icon: Waves },
  { key: 'access24h', label: '24h access', Icon: Clock },
];

const SORTS = [
  { value: 'distance', label: 'Closest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'availability', label: 'Most availability' },
];

export default function SearchFilters() {
  const filters = useSearchStore((s) => s.filters);
  const setFilter = useSearchStore((s) => s.setFilter);
  const toggleInArray = useSearchStore((s) => s.toggleInArray);
  const setPriceRange = useSearchStore((s) => s.setPriceRange);
  const resetFilters = useSearchStore((s) => s.resetFilters);
  const activeCount = useSearchStore((s) => s.getActiveFilterCount());
  const [priceOpen, setPriceOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center gap-1 rounded-full border border-slate-200 p-1">
        <Warehouse className="ml-2 h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
        {SPACE_TYPES.map((type) => {
          const active = filters.spaceTypes.includes(type);
          return (
            <button key={type} type="button" aria-pressed={active}
                    onClick={() => toggleInArray('spaceTypes', type)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                      active ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {SPACE_TYPE_LABELS[type]}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-1 rounded-full border border-slate-200 p-1">
        <Plug className="ml-2 h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
        {AMPS.map((amp) => {
          const active = filters.amps.includes(amp);
          return (
            <button key={amp} type="button" aria-pressed={active}
                    onClick={() => toggleInArray('amps', amp)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                      active ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {amp}A
            </button>
          );
        })}
      </div>

      {TOGGLES.map(({ key, label, Icon }) => (
        <button key={key} type="button" aria-pressed={filters[key]}
                onClick={() => setFilter(key, !filters[key])}
                className={`chip ${filters[key] ? 'chip-active' : ''}`}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </button>
      ))}

      <button type="button" aria-pressed={filters.pullThrough}
              onClick={() => setFilter('pullThrough', !filters.pullThrough)}
              className={`chip ${filters.pullThrough ? 'chip-active' : ''}`}>
        Pull-through
      </button>

      <div className="relative">
        <button type="button" onClick={() => setPriceOpen(!priceOpen)}
                className={`chip ${filters.minPrice != null || filters.maxPrice != null ? 'chip-active' : ''}`}>
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          {filters.minPrice != null || filters.maxPrice != null
            ? `$${filters.minPrice ?? 0}–$${filters.maxPrice ?? '∞'}`
            : 'Monthly price'}
        </button>
        {priceOpen && (
          <div className="absolute left-0 z-30 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
            <p className="mb-3 text-sm font-bold text-slate-900">Monthly rate</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="field-label mb-1" htmlFor="price-min">Min</label>
                <input id="price-min" type="number" min={0} className="field-input"
                       value={filters.minPrice ?? ''}
                       onChange={(e) => setPriceRange(e.target.value === '' ? null : Number(e.target.value), filters.maxPrice)} />
              </div>
              <div>
                <label className="field-label mb-1" htmlFor="price-max">Max</label>
                <input id="price-max" type="number" min={0} className="field-input"
                       value={filters.maxPrice ?? ''}
                       onChange={(e) => setPriceRange(filters.minPrice, e.target.value === '' ? null : Number(e.target.value))} />
              </div>
            </div>
            <button type="button" className="btn-ghost mt-3 w-full py-1.5" onClick={() => setPriceOpen(false)}>Done</button>
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {activeCount > 0 && (
          <button type="button" onClick={resetFilters} className="chip text-slate-500">
            <X className="h-3.5 w-3.5" aria-hidden="true" /> Clear {activeCount}
          </button>
        )}
        <div className="flex items-center gap-1.5">
          <ArrowUpDown className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
          <select aria-label="Sort results" value={filters.sort}
                  onChange={(e) => setFilter('sort', e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white py-1.5 pl-2 pr-7 text-xs font-semibold text-slate-700 outline-none focus:border-brand-500">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}
